<template>
  <div class="invoices">
    <div class="main-wrapper">
      <layout-header></layout-header>
      <layout-sidebar></layout-sidebar>

      <div class="page-wrapper">
        <div class="content container-fluid">
          <div class="page-header">
            <div class="row align-items-center">
              <div class="col">
                <h3 class="page-title">
                  Template Auto Follow Up
                  <span class="afu-beta-inline" title="Fitur masih dalam tahap Beta">BETA</span>
                </h3>
                <p class="text-muted mb-2">
                  Gunakan kembali susunan pesan dan pengaturan alur yang sudah
                  tersimpan.
                </p>
                <ul class="breadcrumb">
                  <li class="breadcrumb-item">
                    <router-link to="/index">Dashboard</router-link>
                  </li>
                  <li class="breadcrumb-item">
                    <router-link to="/auto-follow-up">Auto Follow Up</router-link>
                  </li>
                  <li class="breadcrumb-item active">Template</li>
                </ul>
              </div>
              <div class="col-auto float-right ml-auto">
                <router-link
                  to="/auto-follow-up"
                  class="btn btn-outline-secondary btn-rounded"
                >
                  <i class="fa fa-arrow-left"></i>
                  Kembali ke AFU
                </router-link>
              </div>
            </div>
          </div>

          <div v-if="recoveryFlowId" class="alert alert-warning">
            <strong>Draft mungkin sudah berhasil dibuat.</strong>
            {{ recoveryMessage }}
            <div class="mt-2">
              <button
                type="button"
                class="btn btn-sm btn-outline-secondary mr-2"
                :disabled="recoveryLoading"
                @click="retryDraftDetail"
              >
                <i
                  :class="
                    recoveryLoading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'
                  "
                ></i>
                Muat Ulang Draft
              </button>
              <button type="button" class="btn btn-sm btn-outline-secondary" @click="goToList">
                Buka Daftar AFU
              </button>
            </div>
          </div>

          <div v-if="pageError" class="alert alert-danger">
            <i class="fa fa-exclamation-circle"></i>
            {{ pageError }}
          </div>

          <div class="card afu-filter-card">
            <div class="card-body">
              <div class="row align-items-end">
                <div class="col-lg-8 col-md-7 mb-2 mb-md-0">
                  <label class="afu-filter-label">Cari nama template</label>
                  <input
                    v-model="search"
                    type="text"
                    class="form-control"
                    placeholder="Nama template..."
                    :disabled="loading"
                    @keyup.enter="applySearch"
                  />
                </div>
                <div class="col-lg-4 col-md-5 d-flex">
                  <button
                    type="button"
                    class="btn afu-primary-btn mr-2"
                    :disabled="loading"
                    @click="applySearch"
                  >
                    <i
                      :class="loading ? 'fa fa-spinner fa-spin' : 'fa fa-search'"
                    ></i>
                    Cari
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="loading"
                    @click="resetSearch"
                  >
                    Atur Ulang
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="card afu-list-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between flex-wrap mb-3">
                <div>
                  <h5 class="mb-0">Daftar Template</h5>
                  <small class="text-muted">
                    {{ totalRecords }} template tersedia. Jumlah pesan dan sumber
                    kontak dapat dilihat di Detail.
                  </small>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-secondary"
                  :disabled="loading"
                  @click="loadTemplates"
                >
                  <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
                  Muat Ulang
                </button>
              </div>

              <div v-if="listError" class="alert alert-danger d-flex align-items-center justify-content-between flex-wrap">
                <span>
                  <i class="fa fa-exclamation-triangle"></i>
                  {{ listErrorMessage }}
                </span>
                <button type="button" class="btn btn-sm btn-outline-danger" :disabled="loading" @click="loadTemplates">
                  Coba Lagi
                </button>
              </div>

              <div v-if="loading && !rows.length" class="afu-skeleton">
                <Skeleton :count="6" height="52px" gap="10px" />
              </div>

              <div v-else-if="!loading && !rows.length && !listError" class="afu-empty-state">
                <div class="afu-empty-icon">
                  <i class="fa fa-copy"></i>
                </div>
                <h5>{{ search ? "Template tidak ditemukan" : "Belum ada template" }}</h5>
                <p class="text-muted">
                  {{
                    search
                      ? "Coba gunakan nama template yang lain."
                      : "Simpan konfigurasi alur dari halaman detail AFU untuk membuat template pertama."
                  }}
                </p>
                <router-link
                  v-if="!search"
                  to="/auto-follow-up"
                  class="btn afu-primary-btn"
                >
                  Buka Daftar AFU
                </router-link>
              </div>

              <div v-else-if="rows.length" class="table-responsive">
                <table class="table table-hover custom-table mb-0 afu-table">
                  <thead>
                    <tr>
                      <th>Nama Template</th>
                      <th>Deskripsi</th>
                      <th>Terakhir Diperbarui</th>
                      <th class="text-right">Tindakan</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in rows" :key="row.id">
                      <td>
                        <div class="afu-template-name">{{ row.name || "Tanpa nama" }}</div>
                      </td>
                      <td>
                        <span class="afu-template-desc">
                          {{ row.description || "-" }}
                        </span>
                      </td>
                      <td>{{ formatDate(row.update_datetime) }}</td>
                      <td class="text-right afu-actions-cell">
                        <button
                          type="button"
                          class="btn btn-sm afu-primary-btn mr-1"
                          title="Gunakan Template"
                          :disabled="isRowBusy(row)"
                          @click="openUseModal(row)"
                        >
                          <i
                            :class="
                              usingTemplateId === row.id
                                ? 'fa fa-spinner fa-spin'
                                : 'fa fa-play'
                            "
                          ></i>
                          Gunakan
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-secondary mr-1"
                          title="Lihat Detail Template"
                          :disabled="isRowBusy(row)"
                          @click="openDetail(row)"
                        >
                          <i class="fa fa-eye"></i>
                          Lihat Detail
                        </button>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          title="Hapus Template"
                          :disabled="isRowBusy(row)"
                          @click="deleteTemplate(row)"
                        >
                          <i
                            :class="
                              deletingTemplateId === row.id
                                ? 'fa fa-spinner fa-spin'
                                : 'fa fa-trash'
                            "
                          ></i>
                          Hapus
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-if="rows.length" class="afu-pagination">
                <div class="afu-pagination-left">
                  <span>Tampilkan</span>
                  <select
                    v-model.number="perPage"
                    class="select-to-paginasi"
                    :disabled="loading"
                    @change="onPerPageChange"
                  >
                    <option :value="10">10 / halaman</option>
                    <option :value="20">20 / halaman</option>
                    <option :value="50">50 / halaman</option>
                    <option :value="100">100 / halaman</option>
                  </select>
                  <span>dari {{ totalRecords }} template</span>
                </div>
                <div class="afu-pagination-right">
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage <= 1 || loading }"
                    @click.prevent="onPrev"
                  >
                    Sebelumnya
                  </a>
                  <span class="afu-page-label">Halaman</span>
                  <input
                    v-model.number="pageInput"
                    type="number"
                    min="1"
                    :max="lastPage"
                    class="afu-page-input"
                    :disabled="loading"
                    @keyup.enter="onPageChange(pageInput)"
                  />
                  <span class="afu-page-label">dari {{ lastPage }}</span>
                  <a
                    href="javascript:void(0)"
                    class="btn"
                    :class="{ disabled: currentPage >= lastPage || loading }"
                    @click.prevent="onNext"
                  >
                    Berikutnya
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <a-modal
      :visible="useModalVisible"
      title="Gunakan Template"
      :footer="null"
      :closable="!useSubmitting"
      :mask-closable="false"
      wrap-class-name="afu-template-modal"
      @cancel="closeUseModal"
    >
      <div v-if="useTemplateRow">
        <p>
          Template <strong>{{ useTemplateRow.name || "Tanpa nama" }}</strong>
          akan membuat draft alur baru. Draft belum mengirim pesan sampai Anda
          mengaktifkannya.
        </p>
        <div class="form-group">
          <label>Nama alur baru (opsional)</label>
          <input
            v-model="useFlowName"
            type="text"
            class="form-control"
            maxlength="255"
            placeholder="Nama yang akan dipakai untuk draft"
            :disabled="useSubmitting"
          />
        </div>
        <p class="text-muted small">
          Nomor pengirim dan sumber kontak mengikuti template. Anda dapat
          memeriksanya kembali di form draft sebelum aktivasi.
        </p>
        <div v-if="useError" class="alert alert-danger">
          {{ useError }}
        </div>
        <div class="text-right">
          <button
            type="button"
            class="btn btn-outline-secondary mr-2"
            :disabled="useSubmitting"
            @click="closeUseModal"
          >
            Batal
          </button>
          <button
            type="button"
            class="btn afu-primary-btn"
            :disabled="useSubmitting"
            @click="confirmUseTemplate"
          >
            <i
              :class="useSubmitting ? 'fa fa-spinner fa-spin' : 'fa fa-play'"
            ></i>
            {{ useSubmitting ? "Membuat Draft..." : "Gunakan Template" }}
          </button>
        </div>
      </div>
    </a-modal>

    <a-modal
      :visible="detailModalVisible"
      title="Detail Template"
      :footer="null"
      width="760px"
      wrap-class-name="afu-template-modal"
      @cancel="closeDetail"
    >
      <div v-if="detailLoading" class="afu-detail-loading">
        <i class="fa fa-spinner fa-spin"></i>
        Memuat detail template...
      </div>
      <div v-else-if="detailError" class="alert alert-danger">
        {{ detailError }}
        <button
          type="button"
          class="btn btn-sm btn-outline-danger ml-2"
          @click="retryDetail"
        >
          Coba Lagi
        </button>
      </div>
      <template v-else-if="detailTemplate">
        <h5>{{ detailTemplate.name || "Tanpa nama" }}</h5>
        <p class="text-muted">{{ detailTemplate.description || "Tanpa deskripsi." }}</p>
        <p class="small text-muted">
          Terakhir diperbarui {{ formatDate(detailTemplate.update_datetime) }}
        </p>

        <template v-if="detailTemplate.config">
          <div class="afu-template-summary">
            <div>
              <span>Jam kirim</span>
              <strong>
                {{ detailTemplate.config.sending_start_time || "-" }}–{{ detailTemplate.config.sending_end_time || "-" }}
              </strong>
            </div>
            <div>
              <span>Kontak per pengiriman</span>
              <strong>
                {{ detailTemplate.config.batch_min_contacts || 0 }}–{{ detailTemplate.config.batch_max_contacts || 0 }}
              </strong>
            </div>
            <div>
              <span>Batas per hari</span>
              <strong>{{ detailTemplate.config.daily_contact_limit || 0 }} kontak</strong>
            </div>
            <div>
              <span>Jumlah pesan</span>
              <strong>{{ detailSteps.length }} pesan</strong>
            </div>
          </div>

          <h6 class="mt-4">Rangkaian Pesan</h6>
          <div v-if="!detailSteps.length" class="text-muted small">Belum ada pesan.</div>
          <div
            v-for="step in detailSteps"
            :key="step.step_order"
            class="afu-template-step"
          >
            <strong>Pesan {{ step.step_order }} — {{ step.name || "Tanpa nama" }}</strong>
            <div class="small text-muted">
              {{ messageTypeLabel(step.message_type) }} · Jeda {{ step.delay_value }}
              {{ delayUnitLabel(step.delay_unit) }}
            </div>
            <div v-if="step.message_content" class="afu-template-content">
              {{ step.message_content }}
            </div>
          </div>

          <h6 class="mt-4">Sumber Kontak</h6>
          <div v-if="!detailSources.length" class="text-muted small">Belum ada sumber kontak.</div>
          <span
            v-for="source in detailSources"
            :key="source.source_type + ':' + source.source_id"
            class="afu-source-badge"
          >
            {{ sourceTypeLabel(source.source_type) }}:
            {{ source.source_name || "Sumber kontak" }}
          </span>
        </template>
        <div v-else class="alert alert-warning">
          Konfigurasi template belum tersedia.
        </div>
      </template>
      <div class="text-right mt-3">
        <button type="button" class="btn btn-outline-secondary" @click="closeDetail">
          Tutup
        </button>
      </div>
    </a-modal>
  </div>
</template>

<script>
import LayoutHeader from "@/components/layouts/Header.vue";
import LayoutSidebar from "@/components/layouts/Sidebar.vue";
import { Skeleton } from "vue-loading-skeleton";
import { formatLocalDate } from "@/utils/helper";
import { friendlyTemplateError } from "@/utils/autoFollowUpTemplateError";

const MESSAGE_TYPE_LABELS = {
  text: "Teks",
  image: "Gambar",
  video: "Video",
  document: "Dokumen"
};

const SOURCE_TYPE_LABELS = {
  group: "Grup",
  tag: "Tag",
  manual: "Kontak"
};

const DELAY_UNIT_LABELS = {
  minute: "menit",
  hour: "jam",
  day: "hari"
};

export default {
  name: "auto-follow-up-templates",
  components: {
    LayoutHeader,
    LayoutSidebar,
    Skeleton
  },
  data() {
    return {
      loading: false,
      listError: false,
      listErrorMessage: "",
      pageError: "",
      rows: [],
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      pageInput: 1,
      perPage: 20,
      search: "",
      usingTemplateId: "",
      deletingTemplateId: "",
      useModalVisible: false,
      useTemplateRow: null,
      useFlowName: "",
      useSubmitting: false,
      useError: "",
      recoveryFlowId: "",
      recoveryMessage: "",
      recoveryLoading: false,
      detailModalVisible: false,
      detailLoading: false,
      detailError: "",
      detailTemplate: null,
      detailRow: null
    };
  },
  computed: {
    lastPage() {
      return Math.max(Number(this.totalPages) || 0, 1);
    },
    detailSteps() {
      return this.detailTemplate &&
        this.detailTemplate.config &&
        Array.isArray(this.detailTemplate.config.steps)
        ? this.detailTemplate.config.steps
        : [];
    },
    detailSources() {
      return this.detailTemplate &&
        this.detailTemplate.config &&
        Array.isArray(this.detailTemplate.config.sources)
        ? this.detailTemplate.config.sources
        : [];
    }
  },
  created() {
    this.loadTemplates();
  },
  methods: {
    formatDate(value) {
      const unix = Number(value);
      if (!unix || unix <= 0) {
        return "-";
      }
      return formatLocalDate(unix);
    },
    messageTypeLabel(type) {
      return MESSAGE_TYPE_LABELS[type] || "Pesan";
    },
    sourceTypeLabel(type) {
      return SOURCE_TYPE_LABELS[type] || "Sumber kontak";
    },
    delayUnitLabel(unit) {
      return DELAY_UNIT_LABELS[unit] || "";
    },
    async loadTemplates() {
      if (this.loading) {
        return;
      }
      this.loading = true;
      this.listError = false;
      this.listErrorMessage = "";
      try {
        const response = await this.$store.dispatch(
          "autoFollowUp/templateListAutoFollowUp",
          {
            page: this.currentPage,
            per_page: this.perPage,
            search: (this.search || "").trim()
          }
        );
        const payload = response && response.data ? response.data : {};
        this.rows = Array.isArray(payload.data) ? payload.data : [];
        this.totalRecords = Number(payload.totalRecords) || 0;
        this.totalPages = Number(payload.totalPages) || 0;
        this.currentPage = Number(payload.page) || this.currentPage;
        this.perPage = Number(payload.perPage) || this.perPage;
        this.pageInput = this.currentPage;
      } catch (error) {
        this.listError = true;
        this.listErrorMessage = friendlyTemplateError(
          error,
          "Template belum dapat dimuat. Silakan coba lagi."
        );
      } finally {
        this.loading = false;
      }
    },
    applySearch() {
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadTemplates();
    },
    resetSearch() {
      this.search = "";
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadTemplates();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.pageInput = 1;
      this.loadTemplates();
    },
    onPageChange(page) {
      const target = Math.min(
        Math.max(Number(page) || 1, 1),
        this.lastPage
      );
      if (target === this.currentPage) {
        this.pageInput = this.currentPage;
        return;
      }
      this.currentPage = target;
      this.pageInput = target;
      this.loadTemplates();
    },
    onPrev() {
      if (this.currentPage > 1) {
        this.onPageChange(this.currentPage - 1);
      }
    },
    onNext() {
      if (this.currentPage < this.lastPage) {
        this.onPageChange(this.currentPage + 1);
      }
    },
    isRowBusy(row) {
      return (
        this.loading ||
        this.useSubmitting ||
        !!this.deletingTemplateId ||
        !!this.usingTemplateId ||
        !row ||
        !row.id
      );
    },
    openUseModal(row) {
      if (!row || !row.id || this.usingTemplateId || this.deletingTemplateId) {
        return;
      }
      this.useTemplateRow = row;
      this.useFlowName = row.name || "";
      this.useError = "";
      this.useModalVisible = true;
    },
    closeUseModal() {
      if (this.useSubmitting) {
        return;
      }
      this.useModalVisible = false;
      this.useError = "";
    },
    finishUse() {
      this.useSubmitting = false;
      this.usingTemplateId = "";
    },
    async confirmUseTemplate() {
      if (this.useSubmitting || !this.useTemplateRow) {
        return;
      }
      const templateId = this.useTemplateRow.id;
      if (!templateId) {
        this.useError = "Template tidak valid. Muat ulang daftar template.";
        return;
      }

      this.useSubmitting = true;
      this.usingTemplateId = templateId;
      this.useError = "";

      let response;
      try {
        const payload = { template_id: templateId };
        const name = String(this.useFlowName || "").trim();
        if (name) {
          payload.name = name;
        }
        // template-use tidak idempoten; jangan pernah retry otomatis.
        response = await this.$store.dispatch(
          "autoFollowUp/templateUseAutoFollowUp",
          payload
        );
      } catch (error) {
        this.finishUse();
        const uncertain =
          !error ||
          !error.response ||
          error.code === "ECONNABORTED" ||
          /timeout/i.test(String(error.message || ""));
        this.useError = uncertain
          ? "Template belum dapat digunakan karena koneksi bermasalah. Periksa daftar AFU terlebih dahulu untuk memastikan draft belum dibuat, lalu coba lagi."
          : friendlyTemplateError(
              error,
              "Template belum dapat digunakan. Periksa kembali konfigurasi dan sumber kontaknya."
            );
        return;
      }

      const flow = response && response.data ? response.data : null;
      const flowId = flow && flow.id ? String(flow.id) : "";
      if (!flowId) {
        this.finishUse();
        this.useError =
          "Permintaan sudah dikirim, tetapi ID alur draft tidak diterima. Periksa daftar AFU untuk memastikan draft sudah dibuat.";
        return;
      }

      try {
        const detailResponse = await this.$store.dispatch(
          "autoFollowUp/detailAutoFollowUpSilent",
          { id: flowId }
        );
        const detail =
          detailResponse && detailResponse.data ? detailResponse.data : null;
        if (!detail || !detail.id) {
          throw new Error("Detail draft tidak ditemukan.");
        }
      } catch (error) {
        // Jangan panggil template-use lagi di sini; draft mungkin sudah dibuat.
        this.finishUse();
        this.useModalVisible = false;
        this.recoveryFlowId = flowId;
        this.recoveryMessage =
          "Draft mungkin sudah berhasil dibuat, tetapi detailnya belum dapat dimuat. Silakan buka daftar AFU dan coba muat ulang draft tanpa membuat draft baru.";
        return;
      }

      this.finishUse();
      this.useModalVisible = false;
      this.$fire({
        title: "Draft AFU dibuat",
        text: "Draft AFU berhasil dibuat. Periksa kembali konfigurasi sebelum mengaktifkannya.",
        type: "success",
        timer: 2200
      });
      this.$router.push({
        path: "/auto-follow-up/wizard",
        query: { id: flowId }
      });
    },
    async retryDraftDetail() {
      if (this.recoveryLoading || !this.recoveryFlowId) {
        return;
      }
      this.recoveryLoading = true;
      try {
        const response = await this.$store.dispatch(
          "autoFollowUp/detailAutoFollowUpSilent",
          { id: this.recoveryFlowId }
        );
        const detail = response && response.data ? response.data : null;
        if (!detail || !detail.id) {
          throw new Error("Detail draft tidak ditemukan.");
        }
        const flowId = this.recoveryFlowId;
        this.recoveryFlowId = "";
        this.recoveryMessage = "";
        this.$router.push({
          path: "/auto-follow-up/wizard",
          query: { id: flowId }
        });
      } catch (error) {
        this.recoveryMessage =
          "Detail draft masih belum dapat dimuat. Buka daftar AFU untuk memeriksa draft yang sudah ada.";
      } finally {
        this.recoveryLoading = false;
      }
    },
    goToList() {
      this.$router.push("/auto-follow-up");
    },
    async openDetail(row) {
      if (!row || !row.id || this.isRowBusy(row)) {
        return;
      }
      this.detailModalVisible = true;
      this.detailRow = row;
      this.detailTemplate = null;
      this.detailError = "";
      this.detailLoading = true;
      try {
        const response = await this.$store.dispatch(
          "autoFollowUp/templateDetailAutoFollowUp",
          { id: row.id }
        );
        const template = response && response.data ? response.data : null;
        if (!template || !template.id) {
          throw new Error("Detail template tidak ditemukan.");
        }
        this.detailTemplate = template;
      } catch (error) {
        this.detailError = friendlyTemplateError(
          error,
          "Detail template belum dapat dimuat. Silakan coba lagi."
        );
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetail() {
      this.detailModalVisible = false;
      this.detailTemplate = null;
      this.detailRow = null;
      this.detailError = "";
    },
    retryDetail() {
      if (this.detailRow) {
        this.openDetail(this.detailRow);
      }
    },
    async deleteTemplate(row) {
      if (!row || !row.id || this.isRowBusy(row)) {
        return;
      }
      const result = await this.$fire({
        title: "Hapus template?",
        text:
          (row.name || "Template ini") +
          " akan dihapus dari daftar. Flow yang pernah dibuat dari template ini tidak ikut terhapus.",
        type: "warning",
        showCancelButton: true,
        confirmButtonText: "Ya, hapus",
        cancelButtonText: "Batal",
        confirmButtonColor: "#FE9B44"
      });
      if (!result || !result.value) {
        return;
      }

      this.deletingTemplateId = row.id;
      this.pageError = "";
      let deleted = false;
      try {
        await this.$store.dispatch("autoFollowUp/templateDeleteAutoFollowUp", {
          id: row.id
        });
        deleted = true;
        if (this.rows.length === 1 && this.currentPage > 1) {
          this.currentPage -= 1;
          this.pageInput = this.currentPage;
        }
        await this.loadTemplates();
      } catch (error) {
        this.pageError = friendlyTemplateError(
          error,
          "Template belum dapat dihapus. Silakan coba lagi."
        );
      } finally {
        this.deletingTemplateId = "";
      }
      if (deleted) {
        this.$fire({
          title: "Berhasil",
          text: "Template berhasil dihapus.",
          type: "success",
          timer: 2000
        });
      }
    }
  }
};
</script>

<style scoped>
.afu-beta-inline {
  display: inline-flex;
  align-items: center;
  margin-left: 7px;
  padding: 2px 7px;
  border: 1px solid #ffd8b5;
  border-radius: 999px;
  background: #fff1e5;
  color: #d97a18;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.35;
  letter-spacing: 0.4px;
  vertical-align: middle;
}

.afu-primary-btn {
  background-color: #fe9b44;
  border-color: #fe9b44;
  color: #ffffff;
}

.afu-primary-btn:hover,
.afu-primary-btn:focus {
  background-color: #f08a2e;
  border-color: #f08a2e;
  color: #ffffff;
}

.afu-primary-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.afu-filter-card,
.afu-list-card {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  margin-bottom: 18px;
}

.afu-filter-label {
  display: block;
  color: #6c757d;
  font-size: 12px;
  margin-bottom: 4px;
}

.afu-template-name {
  color: #343a40;
  font-weight: 600;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.afu-template-desc {
  display: block;
  color: #6c757d;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.afu-table {
  min-width: 720px;
}

.afu-table thead th {
  border-top: 0;
  color: #8c8c8c;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  text-transform: uppercase;
  white-space: nowrap;
}

.afu-table tbody td {
  vertical-align: middle;
  font-size: 13px;
}

.afu-actions-cell {
  white-space: nowrap;
}

.afu-empty-state {
  padding: 48px 16px;
  text-align: center;
}

.afu-empty-icon {
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
  border-radius: 50%;
  background: #fff1e5;
  color: #fe9b44;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
}

.afu-empty-state h5 {
  margin-bottom: 8px;
}

.afu-empty-state p {
  max-width: 420px;
  margin: 0 auto 18px;
}

.afu-skeleton {
  padding: 8px 0;
}

.afu-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 16px;
}

.afu-pagination-left,
.afu-pagination-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  color: #6c757d;
  font-size: 13px;
}

.afu-page-label {
  margin: 0 6px;
  color: #6c757d;
  font-size: 13px;
}

.afu-page-input {
  width: 64px;
  height: 34px;
  padding: 4px 6px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  text-align: center;
  display: inline-block;
}

.afu-detail-loading {
  padding: 32px 0;
  text-align: center;
  color: #6c757d;
}

.afu-template-summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 9px;
  background: #f8f9fa;
}

.afu-template-summary div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.afu-template-summary span {
  color: #8c8c8c;
  font-size: 11px;
}

.afu-template-summary strong {
  color: #343a40;
  font-size: 13px;
}

.afu-template-step {
  padding: 10px 0;
  border-bottom: 1px solid #f1f3f5;
}

.afu-template-content {
  margin-top: 5px;
  padding: 8px 10px;
  border-radius: 6px;
  background: #f8f9fa;
  color: #4a5568;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
}

.afu-source-badge {
  display: inline-block;
  margin: 6px 6px 0 0;
  padding: 4px 8px;
  border-radius: 999px;
  background: #e8f0fe;
  color: #1a73e8;
  font-size: 12px;
}

@media (max-width: 767.98px) {
  .afu-template-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .afu-template-name,
  .afu-template-desc {
    max-width: 220px;
  }
}

@media (max-width: 575.98px) {
  .afu-template-name,
  .afu-template-desc {
    max-width: 180px;
  }

  .afu-template-summary {
    grid-template-columns: 1fr;
  }
}
</style>

<style>
.afu-template-modal .ant-modal {
  max-width: calc(100vw - 32px);
}

@media (max-width: 575.98px) {
  .afu-template-modal .ant-modal {
    max-width: calc(100vw - 16px);
    margin: 8px auto;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/autoFollowUp/templates.vue